import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { Add, Edit, Send, PersonAdd } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/adminService';
import RowActionsMenu from './shared/RowActionsMenu';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';

export default function AdminDraftProvidersPage() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '', firstName: '', lastName: '', phone: '', businessName: '', city: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createErrors, setCreateErrors] = useState(null);

  const [invitingId, setInvitingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminService.getDraftProviders(page, 10);
      setProviders(result.providers);
      setPagination(result.pagination);
    } catch (err) {
      setAlert({ severity: 'error', message: 'Hiba a piszkozatok betöltésekor.' });
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleCreateChange = (e) => {
    setCreateForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCreateSubmit = async () => {
    setCreateLoading(true);
    setCreateErrors(null);
    try {
      const response = await adminService.createProvider(createForm);
      const providerId = response?.data?.providerId;
      setCreateDialogOpen(false);
      setCreateForm({ email: '', firstName: '', lastName: '', phone: '', businessName: '', city: '' });
      if (providerId) {
        navigate(`/admin/providers/edit/${providerId}`);
      } else {
        load();
      }
    } catch (err) {
      setCreateErrors(err?.message || 'Hiba történt a létrehozás során.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInvite = async (providerId) => {
    setInvitingId(providerId);
    setAlert(null);
    try {
      await adminService.sendProviderInvite(providerId);
      setAlert({ severity: 'success', message: 'Meghívó email elküldve.' });
      load();
    } catch (err) {
      setAlert({ severity: 'error', message: err?.message || 'Hiba a meghívó küldése során.' });
    } finally {
      setInvitingId(null);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Admin által (vagy a /reszletek oldal "Feliratkozás" formján keresztül) létrehozott, még el nem küldött szolgáltató-profilok.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ bgcolor: '#1b331f', '&:hover': { bgcolor: '#2a5d32' }, whiteSpace: 'nowrap', ml: 2 }}
        >
          Új szolgáltató
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <ResponsiveTableContainer stickyLastColumn>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cégnév</TableCell>
                <TableCell>Kapcsolattartó</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Létrehozva</TableCell>
                <TableCell>Meghívó státusz</TableCell>
                <TableCell>Műveletek</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nincs piszkozat szolgáltató.
                  </TableCell>
                </TableRow>
              ) : (
                providers.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.businessName}</TableCell>
                    <TableCell>{p.user ? `${p.user.firstName} ${p.user.lastName}` : '—'}</TableCell>
                    <TableCell>{p.user?.email}</TableCell>
                    <TableCell>{new Date(p.createdAt).toLocaleDateString('hu-HU')}</TableCell>
                    <TableCell>
                      {p.invitedAt ? (
                        <Chip size="small" color="info" label={`Elküldve: ${new Date(p.invitedAt).toLocaleDateString('hu-HU')}`} />
                      ) : (
                        <Chip size="small" color="default" variant="outlined" label="Még nincs kiküldve" />
                      )}
                    </TableCell>
                    <TableCell>
                      <RowActionsMenu
                        primaryAction={{
                          icon: invitingId === p.id ? <CircularProgress size={16} /> : <Send fontSize="small" />,
                          color: 'primary',
                          iconOnly: true,
                          disabled: invitingId === p.id,
                          title: p.invitedAt ? 'Meghívó újraküldése' : 'Meghívó küldése',
                          onClick: () => handleInvite(p.id),
                        }}
                        menuActions={[
                          { label: 'Profil szerkesztése', icon: <Edit fontSize="small" />, onClick: () => navigate(`/admin/providers/edit/${p.id}`) },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {pagination?.totalPages > 1 && (
            <Box display="flex" justifyContent="center" py={2}>
              <Pagination count={pagination.totalPages} page={page} onChange={(e, p) => setPage(p)} color="primary" />
            </Box>
          )}
        </ResponsiveTableContainer>
      )}

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAdd fontSize="small" /> Új szolgáltató létrehozása
        </DialogTitle>
        <DialogContent>
          {createErrors && <Alert severity="error" sx={{ mb: 2 }}>{createErrors}</Alert>}
          <Typography variant="body2" color="text.secondary" mb={2}>
            A megadott email címre jelszó nélküli fiók jön létre. A profil kitöltése után a "Meghívó küldése" gombbal értesítheted a szolgáltatót.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Email cím" name="email" type="email" required value={createForm.email} onChange={handleCreateChange} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Keresztnév" name="firstName" required value={createForm.firstName} onChange={handleCreateChange} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Vezetéknév" name="lastName" required value={createForm.lastName} onChange={handleCreateChange} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Telefonszám" name="phone" placeholder="+36201234567" value={createForm.phone} onChange={handleCreateChange} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Város" name="city" value={createForm.city} onChange={handleCreateChange} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Cégnév" name="businessName" value={createForm.businessName} onChange={handleCreateChange} helperText="Ha üres, a kereszt- és vezetéknévből generálódik" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Mégse</Button>
          <Button
            variant="contained"
            disabled={!createForm.email || !createForm.firstName || !createForm.lastName || createLoading}
            onClick={handleCreateSubmit}
            sx={{ bgcolor: '#1b331f', '&:hover': { bgcolor: '#2a5d32' } }}
          >
            {createLoading ? <CircularProgress size={20} color="inherit" /> : 'Létrehozás és szerkesztés'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
